import { useEffect } from "react";
import { produce } from "immer";
import { useQueryClient } from "@tanstack/react-query";

import type {
  ChatContactI,
  ChatIncomingMessageI,
  ChatMessageI,
} from "shared/ui/messages/types";
import { useLatestValue } from "shared/lib/hooks";

import { MessagingQueryKeys } from "@/modules/messages/data/keys";
import {
  GetChatInboxV2ResponseI,
  GetMessagingChatHistoryResponseI,
} from "@/api/routes/messaging";
import { CUSTOM_EVENTS } from "@/constants/custom-events";
import { messagingSocketEventHandler } from "@/lib/websockets/messaging/event-handlers";
import { getChatIdentity } from "@/modules/messages/utils";

export const useHandleChatWebSocket = (
  campaignId: string | undefined,
  me: ChatContactI | undefined,
  contact: ChatContactI | undefined
) => {
  const queryClient = useQueryClient();
  const myIdRef = useLatestValue(me?.id);

  // Enter chat when selected chat changes
  useEffect(() => {
    if (campaignId && me?.id && contact?.id) {
      return messagingSocketEventHandler.enterChatV2(
        campaignId,
        contact.id,
        me.id
      );
    }
  }, [campaignId, me?.id, contact?.id]);

  useEffect(() => {
    const handleIncomingMessage = (e: CustomEvent<ChatIncomingMessageI>) => {
      const incomingMessage = e.detail;
      const nowTimestamp = new Date().toISOString();
      const isMyMessage = incomingMessage.sender_id === myIdRef.current;

      const chatId = getChatIdentity({
        campaign_id: incomingMessage.campaign_id,
        customer: {
          id: isMyMessage
            ? incomingMessage.receiver_id
            : incomingMessage.sender_id,
        },
      });

      const formattedChatMessage: ChatMessageI = {
        sender_id: incomingMessage.sender_id,
        message: incomingMessage.message,
        timestamp: nowTimestamp,
      };

      /**
       * Update query data for the chat list. It can be just three flows:
       * 1) The chat doesn't exist yet, create it in query data manually
       * 2) The chat exists, patch its last message and last message timestamp
       * 3) The chat exists, but it's on a different page that is not loaded yet,
       *    so we don't do anything
       */
      queryClient.setQueryData(MessagingQueryKeys.chats(""), (old: any) => {
        // This is the very first chat in the history for a current user
        if (!old?.pages?.[0]?.chat_inbox) {
          const [senderFirstName, senderLastName] =
            incomingMessage.sender_name.split(" ");
          const senderContact = {
            id: incomingMessage.sender_id,
            first_name: senderFirstName,
            last_name: senderLastName,
          };

          const [receiverFirstName, receiverLastName] =
            incomingMessage.receiver_name.split(" ");
          const receiverContact = {
            id: incomingMessage.receiver_id,
            first_name: receiverFirstName,
            last_name: receiverLastName,
          };

          return {
            pages: [
              {
                chat_inbox: [
                  {
                    campaign_id: incomingMessage.campaign_id,
                    customer: isMyMessage ? receiverContact : senderContact,
                    user: isMyMessage ? (me as ChatContactI) : receiverContact,
                    last_message: incomingMessage.message,
                    last_message_timestamp: nowTimestamp,
                    notification_count: 1,
                  },
                ],
              },
            ],
            pageParams: [null],
          };
        }

        const pages = produce(
          [...old.pages] as GetChatInboxV2ResponseI[],
          (oldPages) => {
            for (const page of oldPages) {
              const chatToUpdate = page.chat_inbox?.find(
                (chat) => getChatIdentity(chat) === chatId
              );

              if (chatToUpdate) {
                chatToUpdate.last_message = incomingMessage.message;
                chatToUpdate.notification_count = isMyMessage ? 0 : 1;
                chatToUpdate.last_message_timestamp = nowTimestamp;

                break;
              }
            }
          }
        );

        return {
          ...old,
          pages,
        };
      });

      queryClient.setQueryData(
        MessagingQueryKeys.messages(chatId),
        (old: any) => {
          if (!old?.pages) {
            return {
              pages: [[{ chat_history: [formattedChatMessage] }]],
              pageParams: [null],
            };
          }

          const newPages = [...old.pages];
          newPages[0] = produce(
            newPages[0],
            (draft: GetMessagingChatHistoryResponseI) => {
              draft.chat_history?.push(formattedChatMessage);
            }
          );

          return { ...old, pages: newPages };
        }
      );
    };

    window.document.addEventListener(
      CUSTOM_EVENTS.WEBSOCKETS.MESSAGING.CHAT_RECEIVE,
      handleIncomingMessage as EventListener
    );

    return () => {
      window.document.removeEventListener(
        CUSTOM_EVENTS.WEBSOCKETS.MESSAGING.CHAT_RECEIVE,
        handleIncomingMessage as EventListener
      );
    };
  }, [queryClient]);
};
